// src/pages/sow-analysis/sowAnalysisHelpers.js
//
// Pure helpers for the SOW Analysis page. None of this depends on state or
// the API.

import { normalizeStatusKey } from "../../shared/ui/statusMaps";
import { DIMENSION_KEYWORDS } from "../../utils/constants";

export function safeParseRaw(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/** Qualitative label alongside the numeric score -- same 5-tier
 * thresholds as shared/utils/colorScales.js's getScoreColor, just with
 * words instead of colors. */
export function getScoreLabel(score) {
  if (score === null || score === undefined) return "N/A";
  if (score >= 80) return "Excellent";
  if (score >= 70) return "Good";
  if (score >= 50) return "Fair";
  if (score >= 30) return "Poor";
  return "Critical";
}

/** Normalizes a raw issue severity into the label the severity chart and the issues table both group/filter by. */
export function severityLabel(raw) {
  const trimmed = String(raw || "").trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase() : "Unknown";
}

// Collapses "Partially Covered" / "partially_covered" / "partial" etc. onto
// one of three buckets, the same way StatusBadge's "coverage" map does.
export function coverageBucket(status) {
  const key = normalizeStatusKey(status);
  if (key === "covered") return "covered";
  if (key === "partial" || key === "partiallycovered") return "partial";
  if (key === "missing") return "missing";
  return null;
}

export function bucketRuleDimension(dimensionValue) {
  const lower = String(dimensionValue || "").toLowerCase();
  for (const [bucket, keywords] of Object.entries(DIMENSION_KEYWORDS)) {
    if (keywords.some((kw) => lower.includes(kw))) return bucket;
  }
  return null;
}

// A structured stand-in for the rationale sentences' "X of Y rules
// covered, Z missing (rule, rule, rule)" prose -- computed straight from
// the rule-level evaluations, so it isn't capped at 5 named rules the way
// the LLM-written sentence is, and can be rendered as a chip list instead
// of a wall of rule codes crammed into one sentence.
export function summarizeDimensionCoverage(ruleEvals, bucket) {
  const applicable = ruleEvals.filter((r) => bucketRuleDimension(r.engine_dimension) === bucket);
  const covered = applicable.filter((r) => coverageBucket(r.status) === "covered");
  const partial = applicable.filter((r) => coverageBucket(r.status) === "partial");
  const missing = applicable.filter((r) => coverageBucket(r.status) === "missing");
  return { applicable, covered, partial, missing };
}
